import { useMemo, useState } from 'react'
import useFlightDealLocations from 'home/hooks/useFlightDealLocations'
import { sortBy, uniqueBy } from 'lib/array/arrayUtils'

interface UseFilteredFlightDealsParams {
  flightDeals: Array<App.FlightDeal>
}

interface UseFilteredFlightDealsReturn {
  filteredFlightDeals: Array<App.FlightDeal>
  airports: Array<App.Airport>
  defaultAirport: App.Airport | undefined
  selectedAirport: App.Airport | undefined
  airport: App.Airport | undefined
  setAirport: (airport: App.Airport | undefined) => void
  hasMultipleAirlines: boolean
}

function useFilteredFlightDeals(params: UseFilteredFlightDealsParams): UseFilteredFlightDealsReturn {
  const { flightDeals } = params
  const [airports, defaultAirport] = useFlightDealLocations()
  const [selectedAirport, setAirport] = useState<App.Airport | undefined>()

  const airport = selectedAirport ?? defaultAirport

  const filteredFlightDeals = useMemo(() => {
    if (airport) {
      const deals = flightDeals.filter(({ originAirportCode }) => originAirportCode === airport.code)
      return sortBy(deals, (deal) => deal.hierarchy, 'asc')
    }
    return []
  }, [airport, flightDeals])

  const hasMultipleAirlines = useMemo(
    () => uniqueBy(filteredFlightDeals, (deal) => deal.carrier.code).length > 1,
    [filteredFlightDeals],
  )

  return {
    filteredFlightDeals,
    airports,
    defaultAirport,
    selectedAirport,
    airport,
    setAirport,
    hasMultipleAirlines,
  }
}

export default useFilteredFlightDeals
